import { takeLatest, put, all, call, select } from 'redux-saga/effects';

import history from '~/services/history';
import api from '~/services/api';

import {
  LOGIN_USER,
  LOGOUT_USER,
  TWO_FACTOR_VERIFY,
  TWO_FACTOR_RESEND,
  ENTRAID_CALLBACK,
  ENTRAID_AUTHORIZATION_ERROR,
} from './actionTypes';

import {
  loginSuccess,
  logoutUserSuccess,
  apiError,
  twoFactorRequired,
  twoFactorVerifySuccess,
  twoFactorVerifyError,
  twoFactorResendSuccess,
  twoFactorResendError,
} from './actions';

import { saveUserRequest } from '../../user/actions';

// import { LoadChatsOnLogin, resetChatState } from '../../chat/actions';

import {
  LoadNotificationsRequest,
  // LoadUnreadMsgsRequest,
} from '../../notifications/actions';

function* loginUser({ payload: { email, password } }) {
  try {
    const { data } = yield call(api.post, 'auth/signin', {
      email,
      password,
    });

    if (data?.entraid_required) {
      window.location.href = data.authorize_url;

      return;
    }

    if (data?.two_factor_required) {
      yield put(
        twoFactorRequired({
          email,
          server: data.server || '',
          challengeId: data.challenge_id,
          maskedEmail: data.masked_email,
          expiresIn: data.expires_in,
          codeLength: Number(data.code_length) || 6,
        })
      );

      return;
    }

    const { token, server } = data;

    localStorage.setItem('@VIDDY:Token', token);
    localStorage.setItem('@VIDDY:X-Server', server);

    api.defaults.headers.authorization = `Bearer ${token}`;
    api.defaults.headers['X-Server'] = server;

    yield put(saveUserRequest());
    yield put(loginSuccess());
    yield put(LoadNotificationsRequest());
  } catch (err) {
    const status = err?.response?.status;
    const message = err?.response?.data?.message;

    if (status === 503 && message) {
      yield put(apiError(message));
    } else if (status === 401) {
      yield put(apiError('Credenciais inválidas'));
    } else {
      yield put(apiError(message || 'Erro inesperado. Tente novamente.'));
    }
  }
}

function* verifyTwoFactor({ payload: { code } }) {
  try {
    const twoFactor = yield select(state => state.login.twoFactor);

    const { email, challengeId } = twoFactor;

    const { data } = yield call(api.post, 'auth/2fa/verify', {
      email,
      challenge_id: challengeId,
      code,
    });

    const { token, server } = data;

    localStorage.setItem('@VIDDY:Token', token);
    localStorage.setItem('@VIDDY:X-Server', server);

    api.defaults.headers.authorization = `Bearer ${token}`;
    api.defaults.headers['X-Server'] = server;

    yield put(saveUserRequest());
    yield put(loginSuccess());
    yield put(twoFactorVerifySuccess());
    yield put(LoadNotificationsRequest());
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      'Não foi possível validar o código. Tente novamente.';

    yield put(twoFactorVerifyError(msg));
  }
}

function* resendTwoFactor() {
  try {
    const twoFactor = yield select(state => state.login.twoFactor);

    const { email, challengeId } = twoFactor;

    yield call(api.post, 'auth/2fa/resend', {
      email,
      challenge_id: challengeId,
    });

    yield put(twoFactorResendSuccess());
  } catch (err) {
    const msg =
      err?.response?.data?.message || 'Não foi possível reenviar o código.';

    yield put(twoFactorResendError(msg));
  }
}

function* entraIdCallback({ payload: { code, state } }) {
  try {
    const { data } = yield call(api.post, 'auth/entraid/callback', {
      code,
      state,
    });

    const { token, server } = data;

    localStorage.setItem('@VIDDY:Token', token);
    localStorage.setItem('@VIDDY:X-Server', server);

    api.defaults.headers.authorization = `Bearer ${token}`;
    api.defaults.headers['X-Server'] = server;

    yield put(saveUserRequest());
    yield put(loginSuccess());
    yield put(LoadNotificationsRequest());
  } catch (err) {
    const status = err?.response?.status;
    const message = err?.response?.data?.message;

    history.push('/login', {
      entraIdError:
        status !== 401 && message
          ? message
          : 'Não foi possível concluir o login pela conta Microsoft. Tente novamente ou contate o administrador.',
    });
  }
}

function* entraIdAuthorizationError({
  payload: { state, error, errorDescription },
}) {
  yield call(() =>
    api
      .post('auth/entraid/callback', {
        state,
        error,
        error_description: errorDescription || '',
      })
      .catch(() => null)
  );

  history.push('/login', {
    entraIdError:
      error === 'access_denied'
        ? 'O login pela conta Microsoft foi cancelado.'
        : 'Não foi possível concluir o login pela conta Microsoft. Tente novamente ou contate o administrador.',
  });
}

function* logoutUser() {
  try {
    yield put(logoutUserSuccess());
    delete api.defaults.headers.authorization;
    delete api.defaults.headers['X-Server'];

    const persistentAfterClear = localStorage.getItem('shouldHideNotification');
    localStorage.clear();
    localStorage.setItem('shouldHideNotification', persistentAfterClear);

    try {
      Object.keys(sessionStorage)
        .filter(k => k.startsWith('@VIDDY:pii_reveal:'))
        .forEach(k => sessionStorage.removeItem(k));
    } catch (e) {
      // ignore
    }

    history.push('/login');
  } catch (err) {
    yield put(apiError(err.message));
  }
}

export function* watchUserLogout() {
  yield takeLatest(LOGOUT_USER, logoutUser);
}

export default all([
  takeLatest(LOGIN_USER, loginUser),
  takeLatest(TWO_FACTOR_VERIFY, verifyTwoFactor),
  takeLatest(TWO_FACTOR_RESEND, resendTwoFactor),
  takeLatest(ENTRAID_CALLBACK, entraIdCallback),
  takeLatest(ENTRAID_AUTHORIZATION_ERROR, entraIdAuthorizationError),
  takeLatest(LOGOUT_USER, logoutUser),
]);
